/**
 * Quick Actions Panel Component
 * Displays quick action buttons for common tasks
 * Following PRD Phase 2 specifications (lines 260-263)
 */

import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConnectionStore } from '../../stores/connectionStore';
import { useDashboardStore } from '../../stores/dashboardStore';
import { API_ENDPOINTS } from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';
import {
  MessageSquare,
  BarChart3,
  FileText,
  Download,
  RefreshCw,
  Settings,
  HelpCircle,
  TestTube,
  Plus,
  Eye,
  Zap,
  ArrowRight,
  Rocket
} from 'lucide-react';

interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  action: () => void | Promise<void>;
  variant?: 'default' | 'outline' | 'secondary';
  requiresConnection?: boolean;
  disabled?: boolean;
  badge?: string;
  phase?: number;
}

const QuickActionsPanel: React.FC = () => {
  const navigate = useNavigate();
  const { status: connectionStatus, checkConnection } = useConnectionStore();
  const { refreshDashboard, stats } = useDashboardStore();
  const { toast } = useToast();
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const testTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTestConnection = useCallback(async () => {
    if (isTestingConnection) {
      return; // Prevent multiple simultaneous tests
    }
    
    // Clear any existing timeout
    if (testTimeoutRef.current) {
      clearTimeout(testTimeoutRef.current);
    }
    
    setIsTestingConnection(true);
    
    try {
      await checkConnection(true); // Force refresh for test
      toast({
        title: "Connection tested",
        description: "Connection status has been updated",
      });
    } catch (error) {
      toast({
        title: "Connection test failed",
        description: "Unable to test connection",
        variant: "destructive",
      });
    } finally {
      // Add debounce delay before allowing next test
      testTimeoutRef.current = setTimeout(() => {
        setIsTestingConnection(false);
      }, 3000); // 3 second debounce for test connection
    }
  }, [isTestingConnection, checkConnection, toast]);
  
  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (testTimeoutRef.current) {
        clearTimeout(testTimeoutRef.current);
      }
    };
  }, []);

  const handleRefreshDashboard = async () => {
    try {
      await refreshDashboard();
      toast({
        title: "Dashboard refreshed",
        description: "All dashboard data has been updated",
      });
    } catch (error) {
      toast({
        title: "Refresh failed",
        description: "Unable to refresh dashboard data",
        variant: "destructive",
      });
    }
  };

  const handleExportData = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}${API_ENDPOINTS.dashboard.export}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ format: 'csv' })
      });
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Export failed');
      }
      
      // Download the file
      const downloadUrl = data.data.downloadUrl;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = data.data.filename || 'dashboard-export.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Export completed",
        description: "Dashboard data has been exported successfully",
      });
    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: "Export failed",
        description: error instanceof Error ? error.message : "Unable to export dashboard data",
        variant: "destructive",
      });
    }
  };

  const handleDeployAI = async () => {
    try {
      // Show initial loading toast
      toast({
        title: "Preparing TallyConnector",
        description: "Generating your personalized installer...",
      });
      
      // Get tenant_id from API
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user/tenant_id`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to get tenant ID');
      }
      
      const data = await response.json();
      const tenantId = data.data;
      
      // Request EXE generation from AA which calls TC build script
      const generateResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/tcg/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ tenant_id: tenantId })
      });
      
      if (!generateResponse.ok) {
        const errorData = await generateResponse.json();
        throw new Error(errorData.message || 'Failed to generate installer');
      }
      
      const generateData = await generateResponse.json();
      
      if (!generateData.success) {
        throw new Error(generateData.message || 'Failed to generate installer');
      }
      
      // Download the actual EXE file
      toast({
        title: "TallyConnector Ready",
        description: `Downloading installer for tenant: ${tenantId}. Please run it on your Windows PC with Tally.`,
      });
      
      // Trigger actual file download from AA server
      const downloadUrl = `${import.meta.env.VITE_API_URL}${generateData.data.download_url}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = generateData.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Deploy AI error:', error);
      toast({
        title: "Deployment failed",
        description: "Unable to deploy TallyConnector. Please try again.",
        variant: "destructive",
      });
    }
  };

  const quickActions: QuickAction[] = [
    // Primary Actions (Available in Phase 2)
    {
      id: 'deploy-ai',
      label: 'Deploy AI',
      description: 'Download and install TallyConnector',
      icon: Rocket,
      action: handleDeployAI,
      variant: 'default',
      badge: connectionStatus === 'connected' ? 'Connected' : undefined
    },
    {
      id: 'refresh-dashboard',
      label: 'Refresh Data',
      description: 'Update all dashboard information',
      icon: RefreshCw,
      action: handleRefreshDashboard,
      variant: 'default'
    },
    {
      id: 'test-connection',
      label: 'Test Connection',
      description: 'Check Tally connection status',
      icon: TestTube,
      action: handleTestConnection,
      variant: 'outline'
    },
    {
      id: 'export-data',
      label: 'Export Data',
      description: 'Download dashboard data',
      icon: Download,
      action: handleExportData,
      variant: 'outline'
    },

    // Future Phase Actions (Disabled)
    {
      id: 'chat-ai',
      label: 'Chat with AI',
      description: 'Ask questions about your finances',
      icon: MessageSquare,
      action: () => navigate('/chat'),
      variant: 'secondary',
      requiresConnection: true,
      disabled: true,
      badge: 'Phase 3',
      phase: 3
    },
    {
      id: 'generate-reports',
      label: 'Generate Reports',
      description: 'Create financial reports',
      icon: BarChart3,
      action: () => navigate('/reports'),
      variant: 'secondary',
      requiresConnection: true,
      disabled: true,
      badge: 'Phase 5',
      phase: 5
    },
    {
      id: 'view-audit',
      label: 'View Audit Log',
      description: 'Check system activity history',
      icon: FileText,
      action: () => navigate('/audit'),
      variant: 'outline',
      disabled: true,
      badge: 'Phase 4',
      phase: 4
    },
    {
      id: 'settings',
      label: 'Settings',
      description: 'Configure account preferences',
      icon: Settings,
      action: () => navigate('/settings'),
      variant: 'outline',
      disabled: true,
      badge: 'Phase 6',
      phase: 6
    },
    {
      id: 'help',
      label: 'Help & Support',
      description: 'Get help using AccountsGenie',
      icon: HelpCircle,
      action: () => navigate('/help'),
      variant: 'outline',
      disabled: true,
      badge: 'Soon'
    }
  ];

  const isActionAccessible = (action: QuickAction) => {
    if (action.disabled) return false;
    if (action.requiresConnection && connectionStatus !== 'connected') return false;
    return true;
  };

  const primaryActions = quickActions.filter(action => 
    !action.disabled && action.variant === 'default'
  );

  const secondaryActions = quickActions.filter(action => 
    action.variant !== 'default'
  );

  return (
    <div className="space-y-6">
      {/* Primary Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Zap className="h-5 w-5 text-primary" />
            <span>Quick Actions</span>
          </CardTitle>
          <CardDescription>
            Frequently used actions for managing your data
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="grid gap-3 md:grid-cols-1">
            {primaryActions.map((action) => {
              const ActionIcon = action.icon;
              const accessible = isActionAccessible(action);
              
              return (
                <Button
                  key={action.id}
                  variant={action.variant}
                  onClick={action.action}
                  disabled={!accessible}
                  className="justify-start h-auto p-4"
                >
                  <div className="flex items-center space-x-3 w-full">
                    <ActionIcon className="h-5 w-5 flex-shrink-0" />
                    <div className="flex-1 text-left">
                      <div className="font-medium">{action.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {action.description}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 opacity-50" />
                  </div>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Coming Soon Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Plus className="h-5 w-5 text-muted-foreground" />
            <span>Coming Soon</span>
          </CardTitle>
          <CardDescription>
            Features available in future phases
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="grid gap-2 md:grid-cols-2">
            {secondaryActions.map((action) => {
              const ActionIcon = action.icon;
              const accessible = isActionAccessible(action);
              
              return (
                <div
                  key={action.id}
                  className={cn(
                    "flex items-center space-x-3 p-3 rounded-lg border transition-colors",
                    accessible 
                      ? "hover:bg-muted/50 cursor-pointer" 
                      : "opacity-60 cursor-not-allowed"
                  )}
                  onClick={accessible ? action.action : undefined}
                  title={
                    !accessible && action.requiresConnection
                      ? "Requires Tally connection"
                      : !accessible && action.disabled
                      ? `Coming in ${action.badge}`
                      : action.description
                  }
                >
                  <ActionIcon className="h-4 w-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium truncate">
                        {action.label}
                      </span>
                      {action.badge && (
                        <Badge 
                          variant={accessible ? "secondary" : "outline"}
                          className="text-xs"
                        >
                          {action.badge}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {action.description}
                    </p>
                  </div>
                  
                  {accessible && (
                    <ArrowRight className="h-3 w-3 opacity-50" />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Connection Status Alert */}
      {connectionStatus !== 'connected' && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Eye className="h-4 w-4 text-yellow-600" />
              <div className="flex-1">
                <p className="text-sm text-yellow-800 font-medium">
                  Connection Required
                </p>
                <p className="text-sm text-yellow-700">
                  Some actions require an active Tally connection. Deploy the AccountsGenie connector to unlock all features.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Summary */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">At a Glance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-primary">
                  {stats.totalTransactions}
                </div>
                <div className="text-xs text-muted-foreground">
                  Total Transactions
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  ${stats.cashBalance.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground">
                  Cash Balance
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default QuickActionsPanel;